import { Box, Checkbox, FormControl, FormControlLabel, styled, Tooltip } from "@mui/material";
import { ColumnsPanelTrigger, DataGridPro, DataGridProProps, PropsFromSlot, Toolbar, ToolbarButton } from "@mui/x-data-grid-pro";
import { useContext, useMemo, useState } from "react";
import { AppContext } from "../../../../../../common/contexts/app_context/app_context";
import { useColumnSelectionConfig } from "../../../../../../common/hooks/use_column_selection_config";
import { StatInformation } from "../../../../domain/models/stat_information";
import { GridSlots } from "@mui/x-data-grid";

import ViewColumnIcon from '@mui/icons-material/ViewColumn';


/**
 * CustomToolbar
 */

const CustomToolbar = ({
  onFillCurrentDateChange,
}: PropsFromSlot<GridSlots['toolbar']>) => {
  const [fillDateCheckbox, setFillDateCheckbox] = useState(false);
  return (
    <Toolbar>
      <div>
        <FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={fillDateCheckbox}
                onChange={() => {
                  setFillDateCheckbox(prev => {
                    onFillCurrentDateChange(!prev);
                    return !prev;
                  });
                }}
              />
            }
            label="Preencher data de hoje"
          />
        </FormControl>
      </div>
      <Tooltip title="Colunas">
        <ColumnsPanelTrigger render={<ToolbarButton />}>
          <ViewColumnIcon fontSize="small" />
        </ColumnsPanelTrigger>
      </Tooltip>
    </Toolbar>
  );
}

/**
 * CustomFooter
 */

const parseStat = (v: any): string => {
  if (typeof v === 'number') {
    const perc = v * 100;
    if (perc === 0) return '0,0 %';
    return `${perc.toFixed(2)} %`.replaceAll('.', ',');
  }
  return '0,0 %'
}

export const CustomFooter = ({ statInformation }: PropsFromSlot<GridSlots['footer']>) => {
  return (
    <>
      {statInformation &&
        <div className="w-full flex flex-col justify-center items-end">
          <div className="flex flex-row me-[166px]">
            <p className="flex flex-row justify-center items-center w-[8rem] py-2 bg-[#26C281] font-bold text-white">
              I.A: <span className="ps-2">{parseStat(statInformation.ia)}</span>
            </p>
            <p className="flex flex-row justify-center items-center w-[8rem] py-2 bg-[#ffff00] font-bold text-black">
              I.R: <span className="ps-2">{parseStat(statInformation.ir)}</span>
            </p>
            <p className="flex flex-row justify-center items-center w-[8rem] py-2 bg-[#ffb848] font-bold text-black">
              I.RA: <span className="ps-2">{parseStat(statInformation.ira)}</span>
            </p>
            <p className="flex flex-row justify-center items-center w-[8rem] py-2 bg-[#ef4836] font-bold text-white">
              I.REP: <span className="ps-2">{parseStat(statInformation.ix)}</span>
            </p>
          </div>
        </div>
      }
    </>
  );
}

/**
 * StyledDataGrid
 */
type textSize = "muitoPequeno" | "pequeno" | "medio" | "grande";

const tamanhoConfig: Record<textSize, { fontSize: string; }> = {
  muitoPequeno: { fontSize: "0.70rem" },
  pequeno: { fontSize: "0.75rem" },
  medio: { fontSize: "0.875rem" },
  grande: { fontSize: "1.2rem" },
};

interface Props extends DataGridProProps {
  tableName: string;
  height?: string;
  textSize?: textSize;
  statInformation?: StatInformation;
  onFillCurrentDateChange?: (value: boolean) => void;
}

export const CustomStyledDataGrid = ({
  tableName,
  statInformation,
  height = "60vh",
  disableRowSelectionExcludeModel = true,
  textSize = "medio",
  onFillCurrentDateChange,
  ...props
}: Props) => {
  const [
    columnsVisibilityModel,
    handleColumnsVisibilityModel,
  ] = useColumnSelectionConfig({
    tabela: tableName,
  });

  const mode = useContext(AppContext).theme
  const config = tamanhoConfig[textSize]
  const StyledDataGrid = useMemo(() => {
    return styled(DataGridPro)(({ theme }) => ({
      '& .MuiDataGrid-columnHeader': {
        backgroundColor: '#aeeece',
        fontSize: config.fontSize,
      },
      '& .MuiDataGrid-footerContainer': {
        display: 'none',
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        wordBreak: 'normal',
        whiteSpace: 'normal',
        lineHeight: 1.5,
        color: 'black',
      },
      '& .MuiIconButton-root': {
        color: 'black',
      },
      '& .MuiDataGrid-toolbar .MuiIconButton-root': {
        color: mode === 'light' ? 'black' : 'white',
      },
      '& .MuiDataGrid-columnHeaderTitleContainerContent': {
        '& .MuiCheckbox-root': {
          color: 'black',
        },
      },
      '& .ia-header': {
        backgroundColor: '#26C281',
      },
      '& .ir-header': {
        backgroundColor: 'yellow',
        color: 'black',
      },
      '& .ira-header': {
        backgroundColor: '#FFB848',
        color: 'black',
      },
      '& .ix-header': {
        backgroundColor: '#EF4836',
      },
      backgroundColor: mode === "dark" ? "#2C2C2C" : "#FFFFFF",
    }))
  }, [mode, config]);


  return (
    <Box
      sx={{ display: "flex", height: "auto", width: "100%" }}>
      <StyledDataGrid
        showToolbar={true}
        slots={{
          toolbar: CustomToolbar,
          footer: CustomFooter,
        }}
        autoHeight={true}
        slotProps={{
          columnsPanel: {
            style: {
              color: mode === "dark" ? '#ffffff' : "#000000",
            }
          },
          toolbar: {
            onFillCurrentDateChange,
          } as PropsFromSlot<GridSlots['toolbar']>,
          footer: {
            statInformation,
          } as PropsFromSlot<GridSlots['footer']>,
        }}
        columnVisibilityModel={columnsVisibilityModel}
        disableColumnSorting={true}
        multipleColumnsSortingMode='always'
        onColumnVisibilityModelChange={handleColumnsVisibilityModel}
        disableRowSelectionExcludeModel={true}
        {...props}
        disableColumnMenu={false}
        localeText={{
          noRowsLabel: "Não existem itens para mostrar!",

          columnsManagementSearchTitle: 'Buscar',
          columnsManagementNoColumns: 'Não há colunas',
          columnsManagementShowHideAllText: 'Exibir/Esconder',
          columnsManagementReset: 'Reiniciar',
          columnsManagementDeleteIconLabel: 'Limpar',

          pinToLeft: 'Fixar a esquerda',
          pinToRight: 'Fixar a direita',
          unpin: 'Desfixar',

          columnMenuShowColumns: 'Exibir colunas',
          columnMenuManageColumns: 'Gerenciar colunas',

          columnMenuHideColumn: 'Esconder coluna',
          columnMenuUnsort: 'Desordenar',
          columnMenuSortAsc: 'Ordenar ASC',
          columnMenuSortDesc: 'Ordenar DESC',
        }}
      />
    </Box>
  );
}